import { useFpCampaignProductsService } from '@cart-services/hooks/useFpCampaignProductsService'
import { type Category, CategoryTabs } from '@cart-ui/CategoryTabs/CategoryTabs'
import { useCategoryTabs } from '@cart-ui/CategoryTabs/useCategoryTabs'
import { useFastpassCampaignGoogleAnalyticEvent } from '@dni-analysis'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { Box } from '@pets-ui/Box/Box'
import { Container } from '@pets-ui/Container/Container'
import { Home as HomeIcon } from '@pets-ui/Icons'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import { useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useHistory } from 'react-router-dom-v5'
import { BrandSelector } from './components/BrandSelector/BrandSelector'
import { CatalogInfoLinks } from './components/CatalogInfoLinks/CatalogInfoLinks'
import { CategoryProducts } from './components/CategoryProducts/CategoryProducts'
import { CategoryPromotion } from './components/CategoryPromotion/CategoryPromotion'
import { CountdownProgressBar } from './components/CountdownProgressBar/CountdownProgressBar'
import { ProductCatalogFooter } from './components/ProductCatalogFooter/ProductCatalogFooter'
import { useProductCatalog } from './components/ProductCatalogProvider/useProductCatalog'

const styles = stylex.create({
  root: {
    backgroundColor: colors.grey100,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    paddingBottom: '250px',
  },
})

export const ProductCatalog = () => {
  const history = useHistory()
  const [isExpand, setIsExpand] = useState(false)
  const { projectCode, campaignCode, countryCode, campaignId } = useProductCatalog()
  const { destructuredData: campaign } = useApiCampaignActivityInfo(campaignCode)
  const endTime = campaign?.end_time || null
  const { productsData } = useFpCampaignProductsService(campaignCode)
  const { sendProductCategoryEvent } = useFastpassCampaignGoogleAnalyticEvent({ projectCode, countryCode, campaignId })

  const categoryTabs: Category[] = useMemo(() => {
    const promotionTabs = campaign?.has_homepage ? [{ label: campaign?.homepage_name || '檔期活動', value: 'promotion', icon: <HomeIcon /> }] : []
    const productTabs = productsData?.products?.map(category => ({ label: category.tab_display_name, value: category.tab_display_name, highlight: category.coupon_ids.length > 0 })) || []
    return [...promotionTabs, ...productTabs]
  }, [campaign?.has_homepage, campaign?.homepage_name, productsData?.products])

  const { setCategory, category } = useCategoryTabs(categoryTabs)

  const handleChange = useCallbackEvent((category: Category) => {
    setCategory(category.value)
    history.push(`/campaigns/${campaignCode}?tab=${category.value}`)
    if (category.highlight) {
      sendProductCategoryEvent('fastpass_campaign_click_red_dot_category')
    }
  })

  const handelToggle = useCallbackEvent(() => {
    setIsExpand(!isExpand)
  })

  const isCountdownAvailable = endTime !== null && endTime !== ''
  const isPromotion = category === 'promotion'

  return (
    <div {...stylex.props(styles.root)}>
      <Helmet>
        <title>型錄頁</title>
      </Helmet>
      <BrandSelector />
      {isCountdownAvailable && <CountdownProgressBar endTime={endTime} />}
      <CategoryTabs
        projectCode={projectCode}
        categories={categoryTabs}
        activeCategory={category}
        isExpand={isExpand}
        onChange={handleChange}
        onToggle={handelToggle}
        top={isCountdownAvailable ? 88 : 56}
      />
      <Container gap={4} display="flex" flexDirection="column" px={4} pt={4} pb={24}>
        <Box display={isPromotion ? 'block' : 'none'} id="promotion-tab-container">
          <CategoryPromotion scrollMarginTop={isCountdownAvailable ? 35 : 27} />
        </Box>
        <Box display={isPromotion ? 'none' : 'block'}>
          <CategoryProducts category={category} />
        </Box>
      </Container>
      <CatalogInfoLinks />
      <ProductCatalogFooter />
    </div>
  )
}
